/** Vakansiyalar ro'yxati (eskiz 14). Owner va filial menejeri yaratadi, recruiter o'z driverini topshiradi. */
import { rememberPageSize, urlPageSize } from "@/lib/pageSize";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useMe } from "@/features/auth/hooks";
import { can, type Me } from "@/features/auth/types";
import { useCarrierOptions, useJobCounts, useJobs } from "@/features/carriers/api";
import { JOB_STATUS_LABEL, experienceLabel, statesLabel, type JobStatus } from "@/features/carriers/types";
import { EQUIPMENT, US_STATES } from "@/features/drivers/types";
import { Button } from "@/components/ui/button";
import { FilterSelect, Input } from "@/components/ui/field";
import { StatusPill } from "@/components/ui/badge";
import { Pagination, Table, Td, Th, Tr } from "@/components/ui/table";
import { EmptyState, ErrorState, PageHeader, PageLoading, SimilarNote } from "@/components/ui/misc";
import { JobFormModal } from "./JobFormModal";
import { ContextMenu } from "@/components/ui/context-menu";
import { useJobActions } from "./useJobActions";
import { SectionSwitch } from "@/components/SectionSwitch";

export function JobsPage() {
  const me = useMe();
  if (!me.data) return <PageLoading />;
  return <Jobs me={me.data} />;
}

function Jobs({ me }: { me: Me }) {
  const jobActions = useJobActions();
  const { t } = useTranslation("jobs");
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const carrier = params.get("carrier") ?? "";
  const state = params.get("state") ?? "";
  const equipment = params.get("equipment") ?? "";
  const status = params.get("status") ?? "open"; // "all" = filtrsiz
  const search = params.get("search") ?? "";
  const page = Math.max(1, Number(params.get("page") ?? 1));
  const pageSize = urlPageSize(params.get("page_size"), "jobs");
  const [createOpen, setCreateOpen] = useState(params.get("new") === "1"); // ⌘K dan "Yangi vakansiya"

  const setParam = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== "page") next.delete("page");
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const query = useMemo(
    () => ({
      carrier: carrier || undefined,
      state: state || undefined,
      equipment: equipment || undefined,
      status: status === "all" ? undefined : status,
      search: search || undefined,
      page,
      page_size: pageSize,
      ordering: "-created_at",
    }),
    [carrier, state, equipment, status, search, page, pageSize],
  );
  const jobs = useJobs(query);
  const counts = useJobCounts();
  const carriers = useCarrierOptions();
  const hasFilters = !!(carrier || state || equipment || search || status !== "open");

  return (
    <div>
      <PageHeader
        title={t("title")}
        subtitle={counts.data ? t("subtitle", { open: counts.data.open, filled: counts.data.filled, closed: counts.data.closed }) : "…"}
        actions={
          <>
            <SectionSwitch ariaLabel={t("common:nav.carriers")} items={[{ to: "/carriers", label: t("common:nav.carriers") }, { to: "/jobs", label: t("common:nav.jobs") }]} />
            {can(me, "carriers_edit") && (
              <Button variant="primary" onClick={() => setCreateOpen(true)}>
                {t("newJob")}
              </Button>
            )}
          </>
        }
      >
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <FilterSelect value={carrier} onChange={(e) => setParam("carrier", e.target.value)} className="w-auto min-w-[190px] max-w-[280px]" aria-label={t("filters.carrier")}>
            <option value="">{t("filters.carrierAll")}</option>
            {carriers.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect value={state} onChange={(e) => setParam("state", e.target.value)} className="w-auto min-w-[120px] max-w-[280px]" aria-label={t("filters.state")}>
            <option value="">{t("filters.stateAny")}</option>
            {US_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect value={equipment} onChange={(e) => setParam("equipment", e.target.value)} className="w-auto min-w-[150px] max-w-[280px]" aria-label={t("filters.equipment")}>
            <option value="">{t("filters.equipmentAny")}</option>
            {EQUIPMENT.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect value={status} emptyValue="open" onChange={(e) => setParam("status", e.target.value)} className="w-auto min-w-[130px] max-w-[280px]" aria-label={t("filters.status")}>
            <option value="all">{t("filters.statusAll")}</option>
            {(Object.keys(JOB_STATUS_LABEL) as JobStatus[]).map((s) => (
              <option key={s} value={s}>
                {t(`common:status.${s}`)}
              </option>
            ))}
          </FilterSelect>
          <Input value={search} onChange={(e) => setParam("search", e.target.value)} placeholder={t("filters.searchPlaceholder")} className="w-[220px]" aria-label={t("filters.searchAria")} />
          {hasFilters && (
            <Button variant="link" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
              {t("common:actions.clearFilters")}
            </Button>
          )}
        </div>
      </PageHeader>

      {jobs.isPending ? (
        <PageLoading />
      ) : jobs.isError ? (
        <ErrorState message={jobs.error.message} onRetry={() => jobs.refetch()} />
      ) : jobs.data.results.length === 0 ? (
        <EmptyState title={hasFilters ? t("empty.filtered") : t("empty.none")} description={can(me, "carriers_edit") ? t("empty.manager") : t("empty.viewer")} />
      ) : (
        <>
          <SimilarNote show={jobs.data.fuzzy} />
          <Table>
            <thead>
              <tr>
                <Th>{t("columns.job")}</Th>
                <Th>{t("columns.carrier")}</Th>
                <Th>{t("columns.states")}</Th>
                <Th>{t("columns.experience")}</Th>
                <Th>{t("columns.pay")}</Th>
                <Th>{t("columns.homeTime")}</Th>
                <Th>{t("columns.equipment")}</Th>
                <Th>{t("columns.status")}</Th>
                <Th>{t("columns.candidates")}</Th>
              </tr>
            </thead>
            <tbody>
              {jobs.data.results.map((j) => (
                <ContextMenu key={j.id} items={() => jobActions.items(j)}>
                  <Tr clickable onClick={() => navigate(`/jobs/${j.id}`)}>
                    <Td>
                      <Link to={`/jobs/${j.id}`} className="font-semibold text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
                        {j.title}
                      </Link>
                    </Td>
                    <Td>
                      <Link to={`/carriers/${j.carrier}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>
                        {j.carrier_name}
                      </Link>
                      {j.carrier_status !== "active" && <span className="ml-1 text-[11px] text-muted">({t(`common:status.${j.carrier_status}`).toLowerCase()})</span>}
                      {/* Carrierning bo'sh truck'lari -- driver kerak (carrier profili -> Trucks) */}
                      {j.carrier_trucks_available > 0 && <div className="text-[11px] font-semibold text-success">{t("carriers:trucks.freeCount", { count: j.carrier_trucks_available })}</div>}
                    </Td>
                    <Td>{statesLabel(j.states)}</Td>
                    <Td>{experienceLabel(j.min_experience_months)}</Td>
                    <Td>{j.pay_text || "—"}</Td>
                    <Td>{j.home_time || "—"}</Td>
                    <Td>{j.equipment || "—"}</Td>
                    <Td>
                      <StatusPill status={j.status} />
                    </Td>
                    <Td>
                      <div className="leading-tight">
                        <div>{t("candidates", { count: j.candidates_count })}</div>
                        <div className="text-[11px] text-muted">{t("hiredOf", { hired: j.hired_count, openings: j.openings })}</div>
                      </div>
                    </Td>
                  </Tr>
                </ContextMenu>
              ))}
            </tbody>
          </Table>
          <Pagination page={jobs.data.page} pages={jobs.data.pages} count={jobs.data.count} pageSize={jobs.data.page_size} onPage={(p) => setParam("page", String(p))} onPageSize={(s) => {
            rememberPageSize("jobs", s);
            setParam("page_size", String(s));
          }} label={t("pagination")} />
        </>
      )}

      {jobActions.dialogs}
      <JobFormModal open={createOpen} onOpenChange={setCreateOpen} onSaved={(j) => navigate(`/jobs/${j.id}`)} />
    </div>
  );
}
